import { Box, IconButton, Typography, TextField, Fab, Tooltip, Popover, InputAdornment, useMediaQuery } from "@mui/material"
import { useSelector, useDispatch } from "react-redux";
import { setSelectUser, setMessage, setReplyMessage } from "../messagesSlice";
import VideocamIcon from "@mui/icons-material/Videocam";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DescriptionIcon from '@mui/icons-material/Description';
import { useState } from "react";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ConvoMediaFiles from "./convoFiles";
import WaTemplates from "./waTemplates";
import { submitNewMessageThunk, updateMessagesCountThunk } from "../messagesThunk";
import EmojiPicker from "emoji-picker-react";
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function ConvoFooter({ scrollToLastMsg }) {
    const { typeMessage, isReplyMessage, selectedUser, waMessageChats } = useSelector((state) => state.messages);
    const isValidUser = waMessageChats[0]?.is_24hour_timeline === true ? true : false;

    const dispatch = useDispatch();
    const isMobile = useMediaQuery("(max-width:600px)");

    const [state, setState] = useState({
        openEmoji: null,
        choosenEmoji: "",
        sendTemplate: false,
        popoverFilesTarget: false,
        selectedFiles: [],
        showScrollButton: false,
    });

    const handleSpeedDialChange = (actionName) => {
        const convertToBase64 = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });

        const handleFiles = async (files, e) => {
            const filesWithBase64 = await Promise.all(
                files.map(async (file) => {
                    const base64Data = await convertToBase64(file);
                    const fileSizeInMB = file.size / (1024 * 1024);
                    return {
                        file,
                        base64Data,
                        name: file.name,
                        type: file.type,
                        sizeMB: fileSizeInMB.toFixed(2),
                    };
                })
            );

            setState((prev) => ({
                ...prev,
                selectedFiles: filesWithBase64,
                popoverFilesTarget: true,
            }));
        };

        switch (actionName) {
            case "template":
                setState((prev) => ({ ...prev, sendTemplate: true }));
                break;

            case "photos":
                const photoInput = document.createElement("input");
                photoInput.type = "file";
                // allow only images, video, audio
                photoInput.accept = "image/jpeg,image/jpg,image/png,video/mp4,audio/mp3";
                photoInput.multiple = true;
                photoInput.onchange = (e) => {
                    const files = Array.from(e.target.files).filter((file) => {
                        const allowedTypes = [
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                            "video/mp4",
                            "audio/mp3",
                        ];
                        return allowedTypes.includes(file.type);
                    });
                    handleFiles(files, e);
                };
                photoInput.click();
                break;

            case "documents":
                const docInput = document.createElement("input");
                docInput.type = "file";
                docInput.accept = ".pdf,.doc,.docx,.xls,.xlsx";
                docInput.multiple = true;
                docInput.onchange = (e) => {
                    const files = Array.from(e.target.files).filter((file) => {
                        const allowedExtensions = [".pdf", ".doc", ".docx", ".xls", ".xlsx"];
                        return allowedExtensions.some((ext) =>
                            file.name.toLowerCase().endsWith(ext)
                        );
                    });
                    handleFiles(files, e);
                };
                docInput.click();
                break;

            default:
                break;
        }
    };


    const handlePopoverClose = () => {
        setState((prev) => ({ ...prev, popoverFilesTarget: false, selectedFiles: [] }));
    };

    const removeFile = (index) => {
        setState((prevState) => {
            const updated = [...prevState.selectedFiles];
            if (+updated.length === 0) {
                setState(prev => ({ ...prev, popoverFilesTarget: false }))
            }
            updated.splice(index, 1);
            return { ...prevState, selectedFiles: updated };
        });
    };


    // Open popover
    const handleEmojiOpen = (event) => {
        setState(prev => ({ ...prev, anchorEl: event.currentTarget }));
    };

    // Close popover
    const handleEmojiClose = () => {
        setState(prev => ({ ...prev, anchorEl: null }));
    };

    // Append emoji to message
    const handleEmojiClick = (emojiData) => {
        dispatch(setMessage((typeMessage || "") + emojiData.emoji));
        setState(prev => ({ ...prev, choosenEmoji: emojiData.emoji }));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            dispatch(setSelectUser(null));
            e.preventDefault();
        } else {
            if (e.shiftKey) {
                return
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleMessageSubmit(e);
            }
        }
    };

    const determineMessageType = (uploadedFiles) => {
        if (!uploadedFiles.length) return "text";

        const mimeType = uploadedFiles[0].file_extension_type?.toLowerCase();

        if (!mimeType) return "text";

        if (mimeType.startsWith("image/")) return "image";
        if (mimeType.startsWith("video/")) return "video";
        if (mimeType === "application/pdf") return "document";

        return "text";
    };


    const handleMessageChange = (e) => {
        dispatch(setMessage(e.target.value));
    }

    const handleMessageSubmit = (e, uploadedPath = []) => {
        e.preventDefault();

        const formData = {
            "receiver_contact_id": selectedUser,
            "user_message": typeMessage,
            "reply_msg_wa_id": isReplyMessage?.whatapp_chat_id ?? "",
            "reply_msg_crm_id": isReplyMessage?.id ?? "",
            "file_url": uploadedPath?.map((item) => item.file_url).toString() ?? "",
            "file_id": uploadedPath?.flatMap(item => item.file_id).filter(val => val !== 1).toString() ?? "",
            "type": determineMessageType(uploadedPath) ?? "text",
        }

        // dispatch async thunk
        dispatch(submitNewMessageThunk(formData));
        dispatch(updateMessagesCountThunk());

        // clear input
        handlePopoverClose();
        dispatch(setMessage(""));
        dispatch(setReplyMessage(null));
    };

    return (
        <Box sx={{ position: "sticky", bottom: 0, width: "100%", zIndex: 10 }}>
            {isReplyMessage && (
                <Box sx={{ mx: { xs: 1, sm: 1.5 }, mt: 1, px: 1.5, py: 0.75, borderLeft: "4px solid #2563eb", bgcolor: "#eff6ff", borderRadius: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" sx={{ color: "#1d4ed8", fontWeight: 700 }}>Replying to {isReplyMessage?.user_name ?? "-"}</Typography>
                        <IconButton size="small" onClick={() => dispatch(setReplyMessage(null))}>✕</IconButton>
                    </Box>


                    {isReplyMessage?.media_type === "text" && (
                        <TextField
                            fullWidth
                            multiline
                            disabled
                            minRows={1}
                            maxRows={2}
                            value={isReplyMessage?.content ?? ""}
                            InputProps={{ disableUnderline: true, readOnly: true }}
                            variant="standard"
                            sx={{
                                bgcolor: "transparent",
                                "& .MuiInputBase-root": { border: "none", padding: 0, "& .Mui-disabled": { color: "inherit" } },
                            }}
                        />
                    )}



                    <Box sx={{ mt: 0.5 }}>
                        {/* {isReplyMessage?.content && (
                            <TextField
                                fullWidth
                                multiline
                                disabled
                                minRows={1}
                                maxRows={2}
                                value={isReplyMessage?.content}
                                InputProps={{ disableUnderline: true, readOnly: true }}
                                variant="standard"
                                sx={{ bgcolor: "transparent", "& .MuiInputBase-root": { border: "none", padding: 0 } }}
                            />
                        )} */}

                        {isReplyMessage?.media_type === "location" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "grey.200", borderRadius: 2, width: "fit-content" }}>
                                <LocationPinIcon color="primary" />
                                <Typography variant="body2">Location</Typography>
                            </Box>
                        )}

                        {isReplyMessage?.media_type === "image" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "grey.200", borderRadius: 2, width: "fit-content" }}>
                                <ImageIcon color="primary" />
                                <Typography variant="body2">Photo</Typography>
                            </Box>
                        )}

                        {isReplyMessage?.media_type === "document" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "grey.200", borderRadius: 2, width: "fit-content" }}>
                                <PictureAsPdfIcon color="primary" />
                                <Typography variant="body2">Document</Typography>
                            </Box>
                        )}

                        {isReplyMessage?.media_type === "video" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "grey.200", borderRadius: 2, width: "fit-content" }}>
                                <VideocamIcon color="primary" />
                                <Typography variant="body2">Video</Typography>
                            </Box>
                        )}

                        {isReplyMessage?.media_type === "audio" && isReplyMessage?.media_url && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "grey.200", borderRadius: 2, width: "fit-content" }}>
                                <AudiotrackIcon color="primary" />
                                <Typography variant="body2">Audio</Typography>
                            </Box>
                        )}
                    </Box>

                </Box>
            )}


            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    width: "100%",
                    bgcolor: "#fff",
                    px: { xs: 1, sm: 1.5 },
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    flexDirection: 'row',
                    gap: { xs: 0.75, sm: 1.25 },
                    zIndex: 10,
                }}
            >

                {isReplyMessage === null &&
                    <Fab
                        color="primary"
                        size="small"
                        sx={{
                            position: "absolute", bottom: 66, right: 12,
                            width: 36, height: 36, minHeight: 36,
                            color: "#2563eb",
                            bgcolor: "#fff",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 6px 16px -6px rgba(15,23,42,0.35)",
                            "&:hover": { bgcolor: "#eff6ff" },
                            "& .MuiSvgIcon-root": { fontSize: 22 },
                        }}
                        onClick={scrollToLastMsg}
                    >
                        <KeyboardArrowDownIcon />
                    </Fab>}

                <Box
                    sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        gap: 0.5,
                        position: "relative",
                        height: 40,
                    }}
                >

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Send Template">
                            <IconButton
                                color="primary"
                                size="small"
                                sx={{
                                    width: { xs: 34, sm: 38 },
                                    height: { xs: 34, sm: 38 },
                                    color: '#fff',
                                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                    borderRadius: '10px',
                                    boxShadow: '0 4px 10px -4px rgba(37,99,235,0.6)',
                                    transition: 'transform .15s ease, box-shadow .15s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 20 },
                                }}
                                onClick={() => handleSpeedDialChange('template')}
                                aria-label="Template"
                            >
                                <DescriptionIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={isValidUser ? "Send a template first" : "Attach Image/Video"}>
                            <span style={{ cursor: isValidUser ? "not-allowed" : "pointer", }}>
                                <IconButton
                                    size="small"
                                    sx={{
                                        width: { xs: 32, sm: 38 },
                                        height: { xs: 32, sm: 38 },
                                        color: "#64748b",
                                        transition: "all .15s ease",
                                        "&:hover": { bgcolor: "#eff6ff", color: "#2563eb" },
                                        "&.Mui-disabled": { color: "#cbd5e1" },
                                    }}
                                    onClick={() => handleSpeedDialChange('photos')}
                                    aria-label="Attach image or video"
                                    disabled={isValidUser}
                                >
                                    <AddPhotoAlternateIcon sx={{ fontSize: 22 }} />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title={isValidUser ? "Send a template first" : "Attach Document"}>
                            <span style={{ cursor: isValidUser ? "not-allowed" : "pointer", }}>
                                <IconButton
                                    size="small"
                                    sx={{
                                        width: { xs: 32, sm: 38 },
                                        height: { xs: 32, sm: 38 },
                                        color: "#64748b",
                                        transition: "all .15s ease",
                                        "&:hover": { bgcolor: "#eff6ff", color: "#2563eb" },
                                        "&.Mui-disabled": { color: "#cbd5e1" },
                                    }}
                                    onClick={() => handleSpeedDialChange("documents")}
                                    aria-label="Attach document"
                                    disabled={isValidUser}
                                >
                                    <AttachFileIcon sx={{ fontSize: 22, transform: "rotate(45deg)" }} />
                                </IconButton>
                            </span>
                        </Tooltip>

                    </Box>
                </Box>

                <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                    <span style={{ cursor: isValidUser ? "not-allowed" : "text", display: "block" }}>
                        <TextField
                            disabled={isValidUser}
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={2}
                            value={
                                isValidUser
                                    ? "To message this contact, please send a template message first."
                                    : typeMessage
                            }
                            onChange={handleMessageChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ cursor: isValidUser ? "not-allowed" : "pointer" }}>
                                        {isValidUser ? (
                                            <InfoOutlinedIcon sx={{ color: "#d97706", fontSize: 20 }} />
                                        ) : (
                                            <Tooltip title="Open Emoji">
                                                <InsertEmoticonIcon
                                                    onClick={handleEmojiOpen}
                                                    sx={{ fontSize: 22, color: state.anchorEl ? "#2563eb" : "#94a3b8", transition: "color .15s ease", "&:hover": { color: "#2563eb" } }}
                                                />
                                            </Tooltip>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            placeholder="Type a message"
                            variant="outlined"
                            onKeyDown={handleKeyDown}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    minHeight: 44,
                                    borderRadius: "22px",
                                    pl: 1.75,
                                    pr: 2,
                                    py: 0.75,
                                    fontSize: 14,
                                    bgcolor: isValidUser ? "#fffbeb" : "#f8fafc",
                                    transition: "background-color .15s ease, box-shadow .15s ease",
                                    "& fieldset": { borderWidth: "1.5px", borderColor: isValidUser ? "#fde68a" : "#e2e8f0" },
                                    "&:hover fieldset": { borderColor: isValidUser ? "#fde68a" : "#cbd5e1" },
                                    "&.Mui-focused": { bgcolor: "#fff", boxShadow: "0 0 0 4px rgba(37,99,235,0.12)" },
                                    "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: "1.5px" },
                                    "&.Mui-disabled fieldset": { borderColor: "#fde68a" },
                                },
                                "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#92400e", fontWeight: 600, fontSize: 13.5, cursor: "not-allowed" },
                                // Not-allowed cursor over the whole disabled box (MUI sets "default" on disabled inputs)
                                "& .MuiInputBase-root.Mui-disabled, & .MuiInputBase-root.Mui-disabled *": { cursor: "not-allowed" },
                                "& textarea::placeholder": { color: "#94a3b8", opacity: 1 },
                            }}
                        />
                    </span>
                </Box>

                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                    <span style={{ cursor: isValidUser ? "not-allowed" : "pointer", }}>
                        <IconButton
                            disabled={isValidUser}
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                width: { xs: 40, sm: 44 },
                                height: { xs: 40, sm: 44 },
                                color: "#fff",
                                background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                                boxShadow: "0 6px 14px -6px rgba(37,99,235,0.7)",
                                transition: "transform .15s ease, box-shadow .15s ease",
                                "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #2563eb)", transform: "translateY(-1px)" },
                                "&.Mui-disabled": { color: "#fff", background: "#cbd5e1", boxShadow: "none" },
                                "& .MuiSvgIcon-root": { fontSize: 20, ml: "2px" },
                            }}
                            onClick={(e) => typeMessage === "" ? null : handleMessageSubmit(e)}
                        >
                            <SendIcon />
                        </IconButton>
                    </span>
                </Box>
            </Box>


            <Popover
                open={Boolean(state.anchorEl)}
                anchorEl={state.anchorEl}
                onClose={handleEmojiClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                PaperProps={{
                    sx: { p: 1, borderRadius: 2, maxHeight: 350, overflowY: "auto", width: "min(350px, calc(100vw - 24px))" },
                }}
            >
                <Box sx={{ mt: 2 }}>
                    <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        disableSearchBar
                        disableSkinTonePicker
                        disableAutoFocus={true}
                        native
                        height={360}     // important
                        width="100%"
                        theme="light"
                    />
                </Box>
            </Popover>

            {state.popoverFilesTarget && <ConvoMediaFiles open={state.popoverFilesTarget} handleClose={handlePopoverClose} selectedFiles={state.selectedFiles} removeFile={removeFile} handleMessageSubmit={handleMessageSubmit} />}
            {state.sendTemplate && <WaTemplates show={state.sendTemplate} handleClose={() => setState(prev => ({ ...prev, sendTemplate: false }))} handleMessageSubmit={handleMessageSubmit} />}
        </Box>
    )
}